import React from "react";
import { renderDate, parseDateUtc } from "../../../formatting/dateFormat";
import { hourlyOrDailyDateDifference } from "../../../formatting/dateDifferHoursDaily";
import { formatToPoundsOunces } from "../../../formatting/weightFormat";
import {
  GridRenderCellParams,
  GridActionsCellItem,
  GridRowParams,
  GridEditInputCell,
} from "@mui/x-data-grid-premium";
import { ExtendedGridColDef } from "../../../components/datagrid/overrides";
import BigNumber from "bignumber.js";
import { HtmlCell } from "../../../components/datagrid/renderers/HtmlCell";
import { ExpandButton } from "../../../components/datagrid/components/ExpandButton";
import { trimWithEllipses } from "../../../components/datagrid/formatting";
import {
  getMultiSelectFilterOperators,
  MultiSelectCell,
  MultiSelectEditCell,
} from "../../../components/datagrid/MultiSelectCell";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { BuyBoxInfoCell } from "./customRenderCellComponents/BuyBoxInfoCell";
import BuyboxInfoLegend from "./tooltips/BuyboxInfo";
import BuyboxMarkupLegend from "./tooltips/BuyboxMarkup";
import { getBuyBoxInfoFilterOperators } from "../../../components/datagrid/BuyBoxInfoFilter";

const PRODUCT_CONTACT_OPTIONS = [
  "Patrick Klotzbach",
  "Robyn Baun",
  "Jonathan Briggs",
  "Alex Burns",
  "Kayla Winter",
  "Marissa Carbonell",
];

const renderEditCellCursorStart = (params: any) => (
  <GridEditInputCell
    {...params}
    inputProps={{
      ...(params.inputProps ?? {}),
      onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
        params.inputProps?.onFocus?.(event);
        event.target.setSelectionRange(0, 0);
      },
    }}
  />
);

export const columns: ExtendedGridColDef[] = [
  {
    field: "listing_associated_inventory_count_view.associated_inventory_count",
    headerName: "",
    description: "",
    type: "actions",
    hideable: false,
    width: 50,
    getActions: (params: GridRowParams) => {
      const associatedCount =
        params.row.listing_associated_inventory_count_view
          ?.associated_inventory_count ?? 0;

      // Only show expand button if there are associated inventory items
      if (associatedCount < 1) {
        return [];
      }

      return [<ExpandButton key="expand" rowId={params.id} />];
    },
  },
  {
    field: "amazon_listings.sort",
    headerName: "Sort",
    description: "User designated sort position",
    type: "number",
    valueGetter: (value, row) => row.amazon_listings?.sort,
    editable: true,
  },
  {
    field: "product.product_id",
    headerName: "Product ID",
    description: "Internal YOM product ID",
    type: "number",
    valueFormatter: (value) => value ?? "",
    valueGetter: (value, row) => row.product?.product_id,
  },
  {
    field: "amazon_listings.msku",
    headerName: "MSKU",
    description: "MSKU (Merchant Stock Keeping Unit)",
    valueGetter: (value, row) => row.amazon_listings?.msku,
    editable: true,
  },
  {
    field: "amazon_listings.asin",
    headerName: "ASIN",
    description: "Product ASIN (Amazon Standard Identification Number)",
    valueGetter: (value, row) => row.amazon_listings?.asin,
    editable: true,
  },
  {
    field: "amazon_listings.html",
    headerName: "HTML",
    description: "General HTML column for row",
    valueGetter: (value, row) => row.amazon_listings?.html,
    renderCell: (params: GridRenderCellParams) => (
      <HtmlCell
        value={params.value}
        containerStyle={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      />
    ),
    editable: true,
  },
  {
    field: "amazon_listings.listing_title",
    headerName: "Title",
    description: "Yom 's title for item (not Amazon 's)",
    width: 350,
    disableAutosize: true,
    valueGetter: (value, row) => row.amazon_listings?.listing_title,
    renderCell: (params: GridRenderCellParams) => {
      return (
        <div
          style={{
            whiteSpace: "normal",
            overflow: "hidden",
            textOverflow: "clip",
            lineHeight: "1.4",
            wordBreak: "break-word",
          }}
        >
          {params.value}
        </div>
      );
    },
    editable: true,
  },
  {
    field: "product_families_view.families",
    headerName: "Families",
    description: "Product families this product is a member of.",
    width: 250,
    disableAutosize: true,
    valueGetter: (value, row) => row.product_families_view?.families,
    renderCell: (params: GridRenderCellParams) => {
      return (
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={params.value}
        >
          {params.value}
        </div>
      );
    },
  },
  {
    field: "product.assembly_part_number",
    headerName: "Part Number",
    description: "Parts required to assemble this product",
    valueGetter: (value, row) => row.product?.assembly_part_number,
  },
  {
    field: "amazon_listings.packaged_weight_ounces",
    headerName: "Weight",
    description: "Weight of the product in lbs and ounces",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      return formatToPoundsOunces(params.value);
    },
    valueGetter: (value, row) => row.amazon_listings?.packaged_weight_ounces,
  },
  {
    field: "product.component_count",
    headerName: "Part Qty",
    description: "How many components are in this product",
    type: "number",
    valueGetter: (value, row) => row.product?.component_count,
    editable: true,
    editType: "inline",
  },
  {
    field: "amazon_listings.yom_handling_cost_adjustment",
    headerName: "Handling Cost Adjustment",
    description: "Custom user inputted amount to adjust base handling cost",
    type: "number",
    valueGetter: (value, row) =>
      row.amazon_listings?.yom_handling_cost_adjustment,
    editable: true,
  },
  {
    field: "product.genre",
    headerName: "Genre",
    description: "Genre",
    valueGetter: (value, row) => row.product?.genre,
    editable: true,
  },
  {
    field: "product.product_contact",
    headerName: "Product Contact",
    description: "The team member responsible for this product",
    type: "singleSelect",
    valueOptions: PRODUCT_CONTACT_OPTIONS,
    valueGetter: (value, row) => row.product?.product_contact,
    editable: true,
  },
  {
    field: "media_types",
    headerName: "Media Types",
    description:
      "The media types in this product (Vinyl, Book, CD, DVD, Bluray, 4K)",
    type: "custom",
    valueGetter: (value, row) => row.media_types,
    valueFormatter: (value: { label: string }[] | null | undefined) =>
      Array.isArray(value) ? value.map((v) => v.label).join(", ") : value ?? "",
    renderCell: (params: GridRenderCellParams) => (
      <MultiSelectCell {...params} />
    ),
    renderEditCell: (params) => <MultiSelectEditCell {...params} />,
    filterOperators: getMultiSelectFilterOperators("media_types"),
    editable: true,
  },
  {
    field: "amazon_listings.retired",
    headerName: "Retired",
    description: "Whether or not the product has been retired.",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === 0) return "No";
      if (!data) return null;
      return "Yes";
    },
    valueGetter: (value, row) => row.amazon_listings?.retired,
  },
  {
    field: "amazon_listings.is_competitor_listing",
    headerName: "Competitor Listing",
    type: "boolean",
    description:
      "Whether this listing is tracked for competitor ASIN reference only.",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === 0) return "No";
      if (!data) return null;
      return "Yes";
    },
    valueGetter: (value, row) => row.amazon_listings?.is_competitor_listing,
  },
  {
    field: "amazon_listings.retired_timestamp",
    headerName: "Retired Timestamp",
    description: "When the product was retired.",
    type: "date",
    renderCell: (params: GridRenderCellParams) => renderDate(params.value),
    valueGetter: (value, row) =>
      parseDateUtc(row.amazon_listings?.retired_timestamp),
  },
  {
    field: "amazon_listings.ship_weight",
    headerName: "Ship Wt",
    description: "Weight and Dimensions of a collection",
    valueGetter: (value, row) => row.amazon_listings?.ship_weight,
  },
  {
    field: "product.product_mark",
    headerName: "Product Mark",
    description:
      "Identifier for parents of a collection (.amx / .rmx / .mx / .bmx) ",
    valueGetter: (value, row) => row.product?.product_mark,
    renderCell: (params: GridRenderCellParams) => {
      return <div dangerouslySetInnerHTML={{ __html: params.value }} />;
    },
    renderEditCell: renderEditCellCursorStart,
    editable: true,
  },
  {
    field: "amazon_listings.card_number",
    headerName: "Card#",
    description: "The artcard number for this collection",
    valueGetter: (value, row) => row.amazon_listings?.card_number,
    editable: true,
  },
  {
    field: "product.total_sales",
    headerName: "Total Product Sales",
    description:
      "The total units of this product sold on Amazon over the product's lifetime.",
    type: "number",
    valueGetter: (value, row) => row.product?.total_sales,
  },
  {
    field: "product.90_day_sales",
    headerName: "90 Day Product Sales",
    description: "Units of this product sold on Amazon over the past 90 days.",
    type: "number",
    valueGetter: (value, row) => row.product?.["90_day_sales"],
  },
  {
    field: "product.30_day_sales",
    headerName: "30 Day Product Sales",
    description: "Units of this v sold on Amazon over the past 30 days.",
    type: "number",
    valueGetter: (value, row) => row.product?.["30_day_sales"],
  },
  {
    field: "product.7_day_sales",
    headerName: "7 Day Product Sales",
    description: "Units of this product sold on Amazon over the past 7 days.",
    type: "number",
    valueGetter: (value, row) => row.product?.["7_day_sales"],
  },
  {
    field: "product.ats",
    headerName: "ATS",
    description: "Quantity of product available to ship",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;

      return (
        <span
          title={`Updated at: ${renderDate(
            params.row.product?.ats_updated_at
          )}`}
        >
          {data}
        </span>
      );
    },
    valueGetter: (value, row) => row.product?.ats,
  },
  {
    field: "product.ats_last_increase_timestamp",
    headerName: "ATS Increased",
    description: "Timestamp for when the ATS value last increased",
    type: "date",
    renderCell: (params: GridRenderCellParams) => renderDate(params.value),
    valueGetter: (value, row) =>
      row.product?.ats_last_increase_timestamp
        ? new Date(row.product?.ats_last_increase_timestamp * 1000)
        : null,
  },
  {
    field: "product.master_analyst_inv",
    headerName: "MAI",
    description:
      "Master Analyst Inventory. Current inventory on hand + Inventory On Order (ONO) for all UPCs required to fulfill this row.",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;

      return (
        <span
          title={`Updated at: ${renderDate(
            params.row.product?.mai_updated_at
          )}`}
        >
          {data}
        </span>
      );
    },
    valueGetter: (value, row) => row.product?.master_analyst_inv,
  },
  {
    field: "product.restock_status_calculated",
    headerName: "Restock",
    description:
      "The availability of supply inventory needed to fulfill this selling option from our distributors. IS=In stock, BO=Back Order, OP=Out of Print",
    type: "string",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === "OP")
        return (
          <span
            style={{ color: "#CC0000", fontWeight: 700, fontSize: 17 }}
            title={`Updated at: ${renderDate(
              params.row.product?.restock_updated_at
            )}`}
          >
            OP
          </span>
        );
      if (data === "BO")
        return (
          <span
            style={{ color: "#0066FF", fontWeight: 700, fontSize: 17 }}
            title={`Updated at: ${renderDate(
              params.row.product?.restock_updated_at
            )}`}
          >
            BO
          </span>
        );
      if (data === "IS")
        return (
          <span
            style={{ color: "#339933", fontWeight: 700, fontSize: 17 }}
            title={`Updated at: ${renderDate(
              params.row.product?.restock_updated_at
            )}`}
          >
            IS
          </span>
        );

      return null;
    },
    valueGetter: (value, row) => row.product?.restock_status_calculated,
  },
  {
    field: "product.weeks_until_depleted",
    headerName: "WTD",
    description:
      "Weeks to Depletion. How many weeks of stock do we have on hand or purchased to fulfill this selling option, based on the last 30 days of total page sales?",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === 9999999.9) return "No sales.";
      if (data === 9999999.8) return "Inventory error.";
      return data;
    },
    valueGetter: (value, row) => row.product?.weeks_until_depleted,
  },
  {
    field: "amazon_listings.amazon_stock_weeks_until_depleted",
    headerName: "WTD AF",
    description:
      "How many weeks of stock do we have in Amazon's network to fulfill this selling option, based on the last 30 days of total page sales?",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === 9999999.9) return "No sales.";
      if (data === 9999999.8) return "Inventory error.";
      return data;
    },
    valueGetter: (value, row) =>
      row.amazon_listings?.amazon_stock_weeks_until_depleted,
  },
  {
    field: "product.replacement_cost",
    headerName: "MRCOG-P",
    description:
      "Master replacement cost of goods - purchasable. The lowest possible cost of purchasing all of the supply inventory required to fulfill this selling option, from any suppliers.",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;

      return (
        <span
          title={`Updated at: ${renderDate(
            params.row.product?.rcog_updated_at
          )}`}
        >
          {data.toLocaleString("en-US", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}
        </span>
      );
    },
    valueGetter: (value, row) => row.product?.replacement_cost,
  },
  {
    field: "product.replacement_cost_dist",
    headerName: "MRCOG-IS",
    description:
      "The lowest cost of purchasing all of the supply inventory required to fulfill this selling option, which is in stock with a distributor supplier.",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;

      return (
        <span
          title={`Updated at: ${renderDate(
            params.row.product?.rcog_dist_updated_at
          )}`}
        >
          {data.toLocaleString("en-US", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}
        </span>
      );
    },
    valueGetter: (value, row) => row.product?.replacement_cost_dist,
  },
  {
    field: "product.master_avg_cost_of_goods",
    headerName: "MAVCG",
    description:
      "The sum of the average cost of all in-stock components needed to fulfill this collection.",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;

      return (
        <span
          title={`Updated at: ${renderDate(
            params.row.product?.mavcg_updated_at
          )}`}
        >
          {data.toLocaleString("en-US", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}
        </span>
      );
    },
    valueGetter: (value, row) => row.product?.master_avg_cost_of_goods,
  },
  {
    field: "product.master_quantity_on_order",
    headerName: "MONO",
    description:
      "Master On Order. Current inventory On Order (ONO) for all UPCs required to fulfill this row.",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;

      return (
        <span
          title={`Updated at: ${renderDate(
            params.row.product?.mono_updated_at
          )}`}
        >
          {data}
        </span>
      );
    },
    valueGetter: (value, row) => row.product?.master_quantity_on_order,
  },
  {
    field: "amazon_listings.merchant_available",
    headerName: "Assoc",
    description:
      "Is there a merchant listing available for the ASIN of this row?",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === 0) return "No";
      if (!data) return null;
      return "Yes";
    },
    valueGetter: (value, row) => row.amazon_listings?.merchant_available,
  },
  {
    field: "amazon_listings.purchasable_stock",
    headerName: "Page Stock",
    description: "The total purchasable units for the ASIN's listing page.",
    type: "number",
    valueGetter: (value, row) => row.amazon_listings?.purchasable_stock,
  },
  {
    field: "amazon_listings.30_day_profit",
    headerName: "30 Day Profit",
    description: "The total profit of this ASIN's last 30 day sales.",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;

      const numericUsd = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(data);

      return numericUsd;
    },
    valueGetter: (value, row) => row.amazon_listings?.["30_day_profit"],
  },
  {
    field: "amazon_listings.asin_sales_365_90_30_7",
    headerName: "All Page Sales",
    description: "Page sales in the following format: 365 - 90 - 30 - 7.",
    valueGetter: (value, row) => row.amazon_listings?.asin_sales_365_90_30_7,
  },
  {
    field: "amazon_listings.msku_total_sales",
    headerName: "MSKU Total Sales",
    description: "Total sales for this specific MSKU listing.",
    type: "number",
    valueGetter: (value, row) => row.amazon_listings?.msku_total_sales,
  },
  {
    field: "amazon_listings.asin_total_sales",
    headerName: "Total Page Sales",
    description:
      "The lifetime aggregate sales for YOM's products on this ASIN listing page.",
    type: "number",
    valueGetter: (value, row) => row.amazon_listings?.asin_total_sales,
  },
  {
    field: "amazon_listings.msku_365_day_sales",
    headerName: "MSKU 365 Day Sales",
    description: "365 day sales for this specific MSKU listing.",
    type: "number",
    valueGetter: (value, row) => row.amazon_listings?.msku_365_day_sales,
  },
  {
    field: "amazon_listings.asin_365_day_sales",
    headerName: "365 Day Page Sales",
    description:
      "Aggregate sales for YOM's products on this ASIN listing page over the past 365 days.",
    type: "number",
    valueGetter: (value, row) => row.amazon_listings?.asin_365_day_sales,
  },
  {
    field: "amazon_listings.msku_90_day_sales",
    headerName: "MSKU 90 Day Sales",
    description: "90 day sales for this specific MSKU listing.",
    type: "number",
    valueGetter: (value, row) => row.amazon_listings?.msku_90_day_sales,
  },
  {
    field: "amazon_listings.asin_90_day_sales",
    headerName: "90 Day Page Sales",
    description:
      "Aggregate sales for YOM's products on this ASIN listing page over the past 90 days.",
    type: "number",
    valueGetter: (value, row) => row.amazon_listings?.asin_90_day_sales,
  },
  {
    field: "amazon_listings.msku_30_day_sales",
    headerName: "MSKU 30 Day Sales",
    description: "30 day sales for this specific MSKU listing.",
    type: "number",
    valueGetter: (value, row) => row.amazon_listings?.msku_30_day_sales,
  },
  {
    field: "amazon_listings.asin_30_day_sales",
    headerName: "30 Day Page Sales",
    description:
      "Aggregate sales for YOM's products on this ASIN listing page over the past 30 days.",
    type: "number",
    valueGetter: (value, row) => row.amazon_listings?.asin_30_day_sales,
  },
  {
    field: "amazon_listings.msku_7_day_sales",
    headerName: "MSKU 7 Day Sales",
    description: "7 day sales for this specific MSKU listing.",
    type: "number",
    valueGetter: (value, row) => row.amazon_listings?.msku_7_day_sales,
  },
  {
    field: "amazon_listings.asin_7_day_sales",
    headerName: "7 Day Page Sales",
    description:
      "Aggregate sales for YOM's products on this ASIN listing page over the past 7 days.",
    type: "number",
    valueGetter: (value, row) => row.amazon_listings?.asin_7_day_sales,
  },
  {
    field: "amazon_listings.open_date",
    headerName: "Amazon Listing Created On",
    description: "Date that this listing was created in Amazon.",
    type: "date",
    renderCell: (params: GridRenderCellParams) =>
      renderDate(params.value, "date"),
    valueFormatter: (value) => renderDate(value),
    valueGetter: (value, row) => parseDateUtc(row.amazon_listings?.open_date),
  },
  {
    field: "amazon_listings.created_at",
    headerName: "YOM Listing Created On",
    description: "Date that this listing was created in YOM's system.",
    type: "date",
    renderCell: (params: GridRenderCellParams) => renderDate(params.value),
    valueFormatter: (value) => renderDate(value),
    valueGetter: (value, row) => parseDateUtc(row.amazon_listings?.created_at),
  },
  {
    field: "amazon_product_pricing_info.sales_rank",
    headerName: "Sales Rank",
    description: "Sales rank on Amazon's website",
    valueGetter: (value, row) => row.amazon_product_pricing_info?.sales_rank,
  },
  {
    field: "amazon_product_pricing_info.product_category",
    headerName: "Product Category",
    description: "",
    valueGetter: (value, row) =>
      row.amazon_product_pricing_info?.product_category,
  },
  {
    field: "amazon_product_pricing_info.buy_box_price",
    headerName: "BuyBox",
    description: "The price that is currently listed under the buy box",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;

      const numericUsd = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(data);

      return numericUsd;
    },
    valueGetter: (value, row) => row.amazon_product_pricing_info?.buy_box_price,
  },
  {
    field: "amazon_product_pricing_info.buy_box_info",
    headerName: "Buy Box Info",
    description:
      "Information about the buybox seller, ship time, and various other listing conditions.",
    type: "custom",
    renderHeader: () => <BuyboxInfoLegend />,
    renderCell: BuyBoxInfoCell,
    filterOperators: getBuyBoxInfoFilterOperators(),
    valueGetter: (value, row) => {
      return row.amazon_product_pricing_info?.buy_box_info;
    },
  },
  {
    field: "amazon_listings.buy_box_markup_display",
    headerName: "Buybox Markup",
    description:
      "Percentage markup/markdown relative to children/equivalent products",
    renderHeader: () => <BuyboxMarkupLegend />,
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      const error = params.row.amazon_listings?.buy_box_markup_error;
      const missingPieces =
        params.row.amazon_listings?.buy_box_markup_missing_pieces;

      if (!data && !error && !missingPieces) return null;

      if (error === "Misthread") {
        return "Misthread";
      }

      if (error === "Ineligible") {
        return (
          <b>
            <span style={{ fontSize: "2em" }}> &#8734;</span>
          </b>
        );
      }

      if (error === "Not applicable") {
        return (
          <span style={{ color: "Purple" }}>
            <b>
              <span style={{ fontSize: "1.25em" }}>N/A</span>
            </b>
          </span>
        );
      }

      if (missingPieces) {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <img
              src="https://i.ibb.co/Nsdhbrz/1.png"
              style={{ height: "30px" }}
              alt="image"
            />{" "}
            <b style={{ fontSize: "20px" }}>{missingPieces}</b>
          </div>
        );
      }

      if (data === null) return null;

      const percentage = data.match(/[0-9]+/);

      if (data.includes("Premium")) {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <b>+{percentage[0]}%</b>
            <img
              src="https://i.ibb.co/m9Dj2FY/Green-Arrow.png"
              style={{ color: "green" }}
              alt="Premium"
            />
          </div>
        );
      }

      if (data.includes("Discount")) {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <b>{percentage[0]}%</b>
            <img
              src="https://i.ibb.co/pXbvMqQ/Red-Arrow.png"
              style={{ color: "green" }}
              alt="Discount"
            />
          </div>
        );
      }

      if (data.includes("PAR")) {
        return <b>PAR</b>;
      }

      return data;
    },
    valueGetter: (value, row) => row.amazon_listings?.buy_box_markup_display,
  },
  {
    field: "fba_latest_shipment.created_at",
    headerName: "FBA Last Ship Date",
    description: "Date of the latest non-error FBA shipment for this MSKU",
    valueGetter: (value, row) => row.fba_latest_shipment?.created_at,
    valueFormatter: (value) => renderDate(value),
    type: "dateTime",
    renderCell: (params: GridRenderCellParams) => {
      if (!params.value) return null;
      const shipmentId = params.row.fba_latest_shipment?.shipment_id;
      const href = shipmentId
        ? `/datatables/fba-shipments?shipments_filter_fba_shipments.shipment_id_operator=equals&shipments_filter_fba_shipments.shipment_id_value=${shipmentId}`
        : null;
      return (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {renderDate(params.value)}
          {href && (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <OpenInNewIcon
                sx={{
                  fontSize: 13,
                  verticalAlign: "middle",
                  color: "action.active",
                }}
              />
            </a>
          )}
        </span>
      );
    },
  },
  {
    field: "fba_latest_shipment.quantity",
    headerName: "FBA Last Ship Qty",
    description: "Quantity in the latest non-error FBA shipment for this MSKU",
    type: "number",
    valueGetter: (value, row) => row.fba_latest_shipment?.quantity,
    renderCell: (params: GridRenderCellParams) => {
      if (params.value == null) return null;
      const shipmentId = params.row.fba_latest_shipment?.shipment_id;
      const href = shipmentId
        ? `/datatables/fba-shipments?shipments_filter_fba_shipments.shipment_id_operator=equals&shipments_filter_fba_shipments.shipment_id_value=${shipmentId}`
        : null;
      return (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {params.value}
          {href && (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <OpenInNewIcon
                sx={{
                  fontSize: 13,
                  verticalAlign: "middle",
                  color: "action.active",
                }}
              />
            </a>
          )}
        </span>
      );
    },
  },
  {
    field: "amazon_listings.wn",
    headerName: "WN",
    description: "WN (Warehouse notes)",
    valueGetter: (value, row) => row.amazon_listings?.wn,
    editable: true,
  },
  {
    field: "amazon_listings.wwo",
    headerName: "WWO",
    description: "WWO (Warehouse work orders)",
    valueGetter: (value, row) => row.amazon_listings?.wwo,
    editable: true,
  },
  {
    field: "amazon_listings.listory",
    headerName: "Listory",
    description: "Significant events in the life of a product listing",
    valueGetter: (value, row) => row.amazon_listings?.listory,
    editable: true,
  },
  {
    field: "amazon_listings.pick_status",
    headerName: "Pick Status",
    description: "Pick Status",
    valueGetter: (value, row) => row.amazon_listings?.pick_status,
    renderEditCell: renderEditCellCursorStart,
    editable: true,
  },
  {
    field: "product.master_avg_cogs_ninety_day",
    headerName: "MAVCG-H 90",
    description:
      "The master average cost of goods paid for the product using the AVCG-H 90 value.",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;

      return (
        <span
          title={`Updated at: ${renderDate(
            params.row.product?.mavcg_90_updated_at
          )}`}
        >
          {data.toLocaleString("en-US", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}
        </span>
      );
    },
    valueGetter: (value, row) => row.product?.master_avg_cogs_ninety_day,
  },
  {
    field: "product.last_ninety_day_avg_cogs",
    headerName: "AVCG-H 90",
    description:
      "The average (mean) cost paid for the product in our warehouse in the last 90 days.",
    type: "number",
    valueGetter: (value, row) => row.product?.last_ninety_day_avg_cogs,
  },
  {
    field: "amazon_listings.next_amz_ship_qty",
    headerName: "Next Ship Qty",
    description: "Next Shipment - number of units to be shipped",
    type: "number",
    valueGetter: (value, row) => row.amazon_listings?.next_amz_ship_qty,
    editable: true,
  },
  {
    field: "amazon_listings.ysr_nss_rec",
    headerName: "YSR NSS Rec",
    description: "Yom's new ship stock target value used in YSR holiday calc",
    type: "number",
    valueGetter: (value, row) => row.amazon_listings?.ysr_nss_rec,
  },
  {
    field: "product.yom_purchase_rec",
    headerName: "YPR",
    description: "Yom's purchase recommendation",
    type: "number",
    valueGetter: (value, row) => row.product?.yom_purchase_rec,
  },
  {
    field: "amazon_listings.yom_ship_rec_holiday",
    headerName: "YSR Holiday",
    description: "Yom's recommended ship quantity scaled for holidays",
    type: "number",
    valueGetter: (value, row) => row.amazon_listings?.yom_ship_rec_holiday,
  },
  {
    field: "amazon_listings.yom_ship_rec",
    headerName: "YSR",
    description: "Yom's recommended ship quantity",
    type: "number",
    valueGetter: (value, row) => row.amazon_listings?.yom_ship_rec,
  },
  {
    field: "amazon_listings.amz_recommended_ship_quantity",
    headerName: "ASR",
    description: "Amazon's recommended ship quantity",
    type: "number",
    valueGetter: (value, row) =>
      row.amazon_listings?.amz_recommended_ship_quantity,
  },
  {
    field: "amazon_listings.amz_ship_qty",
    headerName: "Amz Ship Qty",
    description:
      "ASQ (Amazon Ship Quantity) Number of units to be shipped to Amazon's warehouse network from our warehouse ",
    type: "number",
    valueGetter: (value, row) => row.amazon_listings?.amz_ship_qty,
    editable: true,
  },
  {
    field: "amazon_listings.yom_ship_rec_amz_ship_rec_diff",
    headerName: "YSR ASR Diff",
    description:
      "Difference between YOM ship recommendation and AMZ ship recommendation",
    type: "number",
    valueGetter: (value, row) =>
      row.amazon_listings?.yom_ship_rec_amz_ship_rec_diff,
  },
  {
    field: "product_profit_projection_view.yom_ship_rec_potential_profit",
    headerName: "YSR Potential Profit",
    description: "Projected profit from selling YOM ship recommended quantity",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;

      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(data);
    },
    valueGetter: (value, row) =>
      row.product_profit_projection_view?.yom_ship_rec_potential_profit,
  },
  {
    field: "product.30_day_restock_in_stock_count",
    headerName: "30 Day IS Rate",
    description:
      "Total days with restock status = IS in the past 30 days. Reported as # days (%)",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === undefined || data === null) return null;

      const percentage = new BigNumber(data).dividedBy(30).multipliedBy(100);
      return `${data} (${percentage.toFixed(0)}%)`;
    },
    valueGetter: (value, row) => row.product?.["30_day_restock_in_stock_count"],
  },
  {
    field: "amazon_listings.analytics_db_column_1",
    headerName: "Analytics DB 1",
    description: "Experimental column from Kelsey's Analytics DB",
    valueGetter: (value, row) => row.amazon_listings?.analytics_db_column_1,
  },
  {
    field: "amazon_listings.analytics_db_column_2",
    headerName: "Analytics DB 2",
    description: "Experimental column from Kelsey's Analytics DB",
    valueGetter: (value, row) => row.amazon_listings?.analytics_db_column_2,
  },
  {
    field: "amazon_listings.analytics_db_column_3",
    headerName: "Analytics DB 3",
    description: "Experimental column from Kelsey's Analytics DB",
    valueGetter: (value, row) => row.amazon_listings?.analytics_db_column_3,
  },
  {
    field: "amazon_listings.analytics_db_column_4",
    headerName: "Analytics DB 4",
    description: "Experimental column from Kelsey's Analytics DB",
    valueGetter: (value, row) => row.amazon_listings?.analytics_db_column_4,
  },
  {
    field: "amazon_listings.amz_queue",
    headerName: "Amazon Queue",
    description:
      "ASD (Amazon Ship Date) Date units must be shipped to Amazon's warehouse network from our warehouse",
    editable: true,
    width: 500,
    disableAutosize: true,
    renderCell: (params: GridRenderCellParams) => {
      return trimWithEllipses(params.value, 250);
    },
    renderEditCell: renderEditCellCursorStart,
    valueGetter: (value, row) => row.amazon_listings?.amz_queue,
  },
  {
    field: "amazon_listings.orders",
    headerName: "Orders",
    description: "Marking column (General Use) ",
    width: 300,
    disableAutosize: true,
    editable: true,
    renderCell: (params: GridRenderCellParams) => {
      return trimWithEllipses(params.value, 250);
    },
    renderEditCell: renderEditCellCursorStart,
    valueGetter: (value: any, row: any) => row.amazon_listings?.orders,
  } as any,
  {
    field: "amazon_listings.bcp",
    headerName: "BCP",
    description:
      "BCP (Barcode Printed) Whether or not the barcode has been printed for this product (ex. Yes, No)",
    valueGetter: (value, row) => row.amazon_listings?.bcp,
    editable: true,
  },
  {
    field: "amazon_listings.an",
    headerName: "AN",
    description: "AN (Analyst Notes) Notes for the analyst to use",
    valueGetter: (value, row) => row.amazon_listings?.an,
    renderCell: (params: GridRenderCellParams) => {
      return <div dangerouslySetInnerHTML={{ __html: params.value }} />;
    },
    renderEditCell: renderEditCellCursorStart,
    editable: true,
  },
  {
    field: "amazon_listings.pricing_notes",
    headerName: "Pricing Notes",
    description: "Pricing Notes",
    editable: true,
    renderCell: (params: GridRenderCellParams) => {
      return trimWithEllipses(params.value, 250);
    },
    renderEditCell: renderEditCellCursorStart,
    valueGetter: (value, row) => row.amazon_listings?.pricing_notes,
  },
  {
    field: "amazon_listings.threading_notes",
    headerName: "Threading Notes",
    description: "Threading Notes",
    editable: true,
    renderCell: (params: GridRenderCellParams) => {
      return trimWithEllipses(params.value, 250);
    },
    renderEditCell: renderEditCellCursorStart,
    valueGetter: (value, row) => row.amazon_listings?.threading_notes,
  },
  {
    field: "amazon_listings.curation_notes",
    headerName: "Curation Notes",
    description: "Curation Notes",
    editable: true,
    renderCell: (params: GridRenderCellParams) => {
      return trimWithEllipses(params.value, 250);
    },
    renderEditCell: renderEditCellCursorStart,
    valueGetter: (value, row) => row.amazon_listings?.curation_notes,
  },
  {
    field: "amazon_listings.inventory_notes",
    headerName: "Inventory Notes",
    description: "Inventory Notes",
    editable: true,
    renderCell: (params: GridRenderCellParams) => {
      return trimWithEllipses(params.value, 250);
    },
    valueGetter: (value, row) => row.amazon_listings?.inventory_notes,
  },
  {
    field: "amazon_listings.shelf",
    headerName: "Shelf",
    description: "Where the item can be found in our warehouse ",
    valueGetter: (value, row) => row.amazon_listings?.shelf,
    editable: true,
  },
  {
    field: "amazon_listings.alpha1",
    headerName: "Alpha 1",
    description: "See Operations Document for current column use",
    valueGetter: (value, row) => row.amazon_listings?.alpha1,
    editable: true,
  },
  {
    field: "amazon_listings.alpha2",
    headerName: "Alpha 2",
    description: "See Operations Document for current column use",
    valueGetter: (value, row) => row.amazon_listings?.alpha2,
    editable: true,
  },
  {
    field: "amazon_listings.priority",
    headerName: "Priority",
    description: "Priority Order for FBA Shipping Queue",
    valueGetter: (value, row) => row.amazon_listings?.priority,
    renderCell: (params: GridRenderCellParams) => {
      return <div dangerouslySetInnerHTML={{ __html: params.value }} />;
    },
    editable: true,
  },
  {
    field: "amazon_listings.num1",
    headerName: "Num 1",
    description: "See Operations Document for current column use",
    valueGetter: (value, row) => row.amazon_listings?.num1,
    editable: true,
  },
  {
    field: "amazon_listings.num2",
    headerName: "Num 2",
    description: "See Operations Document for current column use",
    valueGetter: (value, row) => row.amazon_listings?.num2,
    editable: true,
  },
  {
    field: "amazon_listings.num3",
    headerName: "Num 3",
    description: "See Operations Document for current column use",
    valueGetter: (value, row) => row.amazon_listings?.num3,
    editable: true,
  },
  {
    field: "amazon_listings.prod_list_type",
    headerName: "Product List Type",
    description: "Product List Type",
    valueGetter: (value, row) => row.amazon_listings?.prod_list_type,
    editable: true,
  },
  {
    field: "amazon_listings.price",
    headerName: "Listing Price",
    description: "Your Online Marketplace's current listing price",
    type: "number",
    valueGetter: (value, row) => row.amazon_listings?.price,
  },
  {
    field: "amazon_listings.fnsku",
    headerName: "FNSKU",
    description: "FNSKU (Amazon's Fulfillment Network SKU) ",
    valueGetter: (value, row) => row.amazon_listings?.fnsku,
    editable: true,
  },
  {
    field: "amazon_listings.stock",
    headerName: "Stock",
    description: "Purchasable quantity available on Amazon ",
    type: "number",
    valueGetter: (value, row) => row.amazon_listings?.stock,
  },
  {
    field: "amazon_listings.status",
    headerName: "Status",
    description:
      "Your Online Marketplace's listing status (F - FBA, M - Merchant, .A - Active, .Inactive, .Incomplete) ",
    valueGetter: (value, row) => row.amazon_listings?.status,
  },
  {
    field: "amazon_listings.last_non_cancelled_order_timestamp",
    headerName: "Time Since Last Order",
    description:
      "How many days since Your Online Marketplace has sold this product ",
    type: "date",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;

      return hourlyOrDailyDateDifference(new Date(), data);
    },
    valueGetter: (value, row) =>
      parseDateUtc(row.amazon_listings?.last_non_cancelled_order_timestamp),
  },
  {
    field: "amazon_listings.ship_stock",
    headerName: "NSS",
    description:
      "New Shipped Stock = Available + FC Transfer + FC Processing + Working + Shipped + Receiving ",
    type: "number",
    valueGetter: (value, row) => row.amazon_listings?.ship_stock,
  },
  {
    field: "amazon_listings.inv_age_271_to_365",
    headerName: "Inv Age 271- 365",
    description:
      "Number of unsold units at Amazon's warehouse that have been there for more than 270 days",
    type: "number",
    valueGetter: (value, row) => row.amazon_listings?.inv_age_271_to_365,
  },
  {
    field: "amazon_listings.alert",
    headerName: "Alert",
    description:
      "Amazon's recommendation for fixing a poor preforming listing ",
    valueGetter: (value, row) => row.amazon_listings?.alert,
  },
  {
    field: "amazon_listings.fba_sell_through",
    headerName: "FBA Sell Thru",
    description:
      "Units sold and shipped over the past 90 days divided by the average number of units available at fulfillment centers during that time period. A higher number means we sell through inventory fast, and vice versa. ",
    type: "number",
    valueGetter: (value, row) => row.amazon_listings?.fba_sell_through,
  },
  {
    field: "amazon_listings.cogs",
    headerName: "COGS",
    description:
      "COGS (Cost of Goods Sold) Price Your Online Marketplace paid for our goods, inputed by a analyst",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data !== null && data !== undefined) {
        return `$${(data / 100).toFixed(2)}`;
      } else {
        return data;
      }
    },
    valueGetter: (value, row) => row.amazon_listings?.cogs,
    editable: true,
  },
  {
    field: "amazon_listings.cogs_update_timestamp",
    headerName: "COGS Timestamp",
    description:
      "Shows the timestamp of the last time COGS was updated for a row.",
    type: "date",
    renderCell: (params: GridRenderCellParams) => renderDate(params.value),
    valueGetter: (value, row) =>
      row.amazon_listings?.cogs_update_timestamp
        ? new Date(row.amazon_listings?.cogs_update_timestamp * 1000)
        : null,
  },
  {
    field: "amazon_listings.type",
    headerName: "Type",
    description:
      "Representation if the product was repriced or prospected using our Amazon pricing calculator",
    valueGetter: (value, row) => row.amazon_listings?.type,
  },
  {
    field: "amazon_listings.margin",
    headerName: "Margin",
    description: "Profit margin percentage",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data !== null && data !== undefined) {
        return `${data.toFixed(1)}%`;
      } else {
        return data;
      }
    },
    valueGetter: (value, row) => row.amazon_listings?.margin,
  },
  {
    field: "amazon_listings.profit",
    headerName: "UPC Profit",
    description:
      "Profit that we get if we were to sell the product (Selling Price - COGS - Amazon fees - Your Online Marketplace Warehouse & Handling fees)",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data !== null && data !== undefined) {
        return `$${(data / 100).toFixed(2)}`;
      } else {
        return data;
      }
    },
    valueGetter: (value, row) => row.amazon_listings?.profit,
  },
  {
    field: "amazon_listings.proceeds",
    headerName: "Proceeds",
    description:
      "Selling Price - Amazon fees directly attributable to the sale and fulfillment of an FBA order (inc. referral, fulfillment and closing fees)",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data !== null && data !== undefined) {
        return `$${(data / 100).toFixed(2)}`;
      } else {
        return data;
      }
    },
    valueGetter: (value, row) => row.amazon_listings?.proceeds,
  },
  {
    field: "amazon_listings.roi",
    headerName: "ROI",
    description:
      "ROI (Return on Investment) The gain or loss on an investment relative to the amount of money invested (COGS = Warehousing Fee)",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data !== null && data !== undefined) {
        return `${data.toFixed(1)}%`;
      } else {
        return data;
      }
    },
    valueGetter: (value, row) => row.amazon_listings?.roi,
  },
  {
    field: "amazon_listings.margin_0_pricing",
    headerName: "List 0",
    description: "Selling Price required to achieve a 0% margin",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data !== null && data !== undefined) {
        return `$${(data / 100).toFixed(2)}`;
      } else {
        return data;
      }
    },
    valueGetter: (value, row) => row.amazon_listings?.margin_0_pricing,
  },
  {
    field: "amazon_listings.margin_15_pricing",
    headerName: "List 15",
    description: "Selling Price required to achieve a 15% margin",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data !== null && data !== undefined) {
        return `$${(data / 100).toFixed(2)}`;
      } else {
        return data;
      }
    },
    valueGetter: (value, row) => row.amazon_listings?.margin_15_pricing,
  },
  {
    field: "amazon_listings.margin_30_pricing",
    headerName: "List 30",
    description: "Selling Price required to achieve a 30% margin",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data !== null && data !== undefined) {
        return `$${(data / 100).toFixed(2)}`;
      } else {
        return data;
      }
    },
    valueGetter: (value, row) => row.amazon_listings?.margin_30_pricing,
  },
  {
    field: "keepa_product_data.keepa_buybox_30_day_avg",
    headerName: "Keepa 30d",
    description: "Keepa 30 day buybox average",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      const formatCurrency = (data: any) =>
        data ? `$${Number(data).toFixed(2)}` : "";
      return formatCurrency(data);
    },
    valueGetter: (value, row) =>
      row.keepa_product_data?.keepa_buybox_30_day_avg,
  },
  {
    field: "keepa_product_data.keepa_30_day_sales_range",
    headerName: "Keepa 30d Sales",
    description: "Keepa 30 day sales range",
    valueGetter: (value, row) =>
      row.keepa_product_data?.keepa_30_day_sales_range,
  },
  {
    field: "amazon_listings.yom_market_share",
    headerName: "YOM Market Share",
    description: "YOM Market Share",
    valueGetter: (value, row) => row.amazon_listings?.yom_market_share,
  },
  {
    field: "amazon_listings.yom_buybox_30_day_avg",
    headerName: "YOM 30d Avg",
    description: "YOM 30 day average",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      const formatCurrency = (data: any) =>
        data ? `$${Number(data).toFixed(2)}` : "";
      return formatCurrency(data);
    },
    valueGetter: (value, row) => row.amazon_listings?.yom_buybox_30_day_avg,
  },
  {
    field: "product.master_avg_replacement_cost_diff",
    headerName: "MAVCG - MRCOG-IS",
    description: "MAVCG - MRCOG-IS",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      const formatCurrency = (data: any) =>
        data !== null ? `$${Number(data).toFixed(2)}` : "";
      return formatCurrency(data);
    },
    valueGetter: (value, row) => row.product?.master_avg_replacement_cost_diff,
  },
  {
    field: "keepa_product_data.keepa_timestamp",
    headerName: "Keepa TimeStamp",
    description: "When was the data pulled from Keepa API",
    type: "date",
    renderCell: (params: GridRenderCellParams) => renderDate(params.value),
    valueGetter: (value, row) =>
      row.keepa_product_data?.keepa_timestamp
        ? new Date(row.keepa_product_data.keepa_timestamp * 1000)
        : null,
  },
  {
    field: "amazon_listings.calculator_timestamp",
    headerName: "Calculator TimeStamp",
    description: "When a product was last prospected or priced",
    type: "date",
    renderCell: (params: GridRenderCellParams) => renderDate(params.value),
    valueGetter: (value, row) =>
      row.amazon_listings?.calculator_timestamp
        ? new Date(row.amazon_listings?.calculator_timestamp * 1000)
        : null,
  },
  {
    field: "product.created_at",
    headerName: "Product Created On",
    description: "When this product row was created in YOM's system",
    type: "date",
    renderCell: (params: GridRenderCellParams) => renderDate(params.value),
    valueGetter: (value, row) => parseDateUtc(row.product?.created_at),
  },
  {
    field: "product.short_term_demand_trend_percentage",
    headerName: "ST Demand %",
    description: "Listing Demand Short Term Demand Trend Percentage",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === undefined || data === null) return null;
      return `${data}%`;
    },
    valueGetter: (value, row) =>
      row.product?.short_term_demand_trend_percentage,
  },
  {
    field: "product.medium_term_demand_trend_percentage",
    headerName: "MT Demand %",
    description: "Listing Demand Medium Term Demand Trend Percentage",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === undefined || data === null) return null;
      return `${data}%`;
    },
    valueGetter: (value, row) =>
      row.product?.medium_term_demand_trend_percentage,
  },
  {
    field: "amazon_listings.fba_priority_queue",
    headerName: "FBA Priority Queue",
    description:
      "Identifies which items are part of today's FBA priority shipment.",
    valueGetter: (value, row) => row.amazon_listings?.fba_priority_queue,
  },
];

export const nestedTableColumns: string[] = [
  "supply.sort",
  "supply.upc",
  "supply.html",
  "supply.supply_title",
  "supply.supply_asin",
  "inventory.inventory_shelf",
  "supply_families_view.families",
  "inventory.part_number",
  "inventory_with_formatted_id_view.formatted_inventory_id",
  "inventory.inventory_purchase_recommendation",
  "supply.discontinued_date",
  "supply.studio",
  "supply.ebay_itemid",
  "supply.walsku",
  "supply.restock_status_ftp",
  "supply.sup_lin",
  "supply.listed",
  "supply.ono",
  "inventory.avcg",
  "inventory.ai_pooled",
  "supply.house",
  "inventory.add_date",
  "inventory.sub_date",
  "inventory.inv",
  "supply.dist_inv",
  "supply.dau_inv",
  "supply.dmg_inv_display",
  "amazon_product_pricing_info.buy_box_price",
  "amazon_product_pricing_info.buy_box_info",
  "supply.dtb1",
  "supply.dtb2",
  "supply.dtb3",
  "supply.dtb4",
  "supply.dtb5",
  "supply.dtb6",
  "supply.dtb7",
  "supply.dtb8",
  "supply.alliance",
  "supply.amazon",
  "supply.ccvideo",
  "supply.dealsareus",
  "supply.deepdiscount",
  "supply.discount_entertainment",
  "supply.popmarket",
  "supply.inetvideo",
  "supply.importcds",
  "supply.mila",
  "supply.gruv",
  "supply.wildcard1",
  "supply.wildcard2",
  "supply.rarewaves",
  "supply.target_store",
  "supply.vei",
  "supply.walmart",
  "supply.wowhd",
  "supply.supply_notes",
  "supply_media_view.families",
];
